import type { AppError } from "@/shared/api";

export type MasterServiceErrorSurface = "field" | "form" | "notFound" | "toast" | "stale";

/**
 * Типобезпечні i18n-ключі помилок послуг майстра. Включають доменні
 * `masters.services.errors.*` та generic `errors.*` (ADR-0010 Follow-up).
 * Також покривають ключі politeMessage та unknownService/unknownMaster fallback.
 */
export type MasterServiceI18nKey =
  | "masters.services.saved"
  | "masters.services.removed"
  | "masters.services.unknownService"
  | "masters.services.unknownMaster"
  | "masters.services.errors.invalidDuration"
  | "masters.services.errors.invalidPrice"
  | "masters.services.errors.invalidBuffer"
  | "masters.services.errors.serviceGone"
  | "masters.services.errors.linkGone"
  | "masters.services.errors.serviceInUse"
  | "errors.notFound"
  | "errors.forbidden"
  | "errors.network"
  | "errors.server";

export interface MappedMasterServiceError {
  surface: MasterServiceErrorSurface
  field?: "service" | "duration" | "price" | "buffer"
  i18nKey: MasterServiceI18nKey
}

/**
 * Чиста `AppError → MappedMasterServiceError`. Без router/store/i18n — лише
 * класифікація. Контекст `'submit'` / `'detach'` визначає семантику notFound-гілки.
 */
export function mapMasterServiceError(
  err: AppError,
  context: "submit" | "detach",
): MappedMasterServiceError {
  switch (err.kind) {
    case "validation":
      if (err.code === "invalid_duration") {
        return { surface: "field", field: "duration", i18nKey: "masters.services.errors.invalidDuration" };
      }
      if (err.code === "invalid_price") {
        return { surface: "field", field: "price", i18nKey: "masters.services.errors.invalidPrice" };
      }
      if (err.code === "invalid_buffer") {
        return { surface: "field", field: "buffer", i18nKey: "masters.services.errors.invalidBuffer" };
      }
      // invalid_body / invalid_id / інший validation-код → form-рівень (дрейф контракту).
      return { surface: "form", i18nKey: "errors.server" };

    case "notFound":
      if (context === "submit") {
        if (err.code === "service_not_found") {
          return { surface: "stale", i18nKey: "masters.services.errors.serviceGone" };
        }
        // master_not_found або будь-який інший notFound у submit
        return { surface: "notFound", i18nKey: "errors.notFound" };
      }
      // context === 'detach'
      if (err.code === "master_service_not_found") {
        return { surface: "stale", i18nKey: "masters.services.errors.linkGone" };
      }
      // master_not_found у detach
      return { surface: "notFound", i18nKey: "errors.notFound" };

    case "conflict":
      if (err.code === "master_service_in_use") {
        return { surface: "form", i18nKey: "masters.services.errors.serviceInUse" };
      }
      return { surface: "form", i18nKey: "errors.server" };

    case "forbidden":
      return { surface: "toast", i18nKey: "errors.forbidden" };

    case "network":
      return { surface: "form", i18nKey: "errors.network" };

    case "server":
      return { surface: "form", i18nKey: "errors.server" };

    default:
      return { surface: "form", i18nKey: "errors.server" };
  }
}
